// Weather integration via Open-Meteo (free, no API key required).
// Two-stage flow:
//   1. Resolve the user's profile zip → lat/lon. PREFERRED source is our
//      own `public.zip_codes` table in Supabase (~42k US zips from
//      GeoNames, see migration 0029). Falls back to Open-Meteo's
//      geocoding API for international zips or anything not in our table.
//      Cached forever per zip in localStorage.
//   2. Fetch current weather for those coords. Cached ~1 hour per location.
// If the user hasn't set a zip yet, useWeather returns null and the UI
// hides the tile.
//
// Why NOT Open-Meteo's geocoding for US zips: that API treats the zip
// as a place-name search (`name=10001`), not a postal-code lookup, and
// returns wildly inconsistent results for common 5-digit US zips —
// sometimes the right city, sometimes a town in another state with
// "10001" in its name. Our local `zip_codes` table is the source of
// truth: deterministic, fast, exactly correct city + state + lat/lng.
//
// In v1.1 we'll switch to a server-side cron that pre-computes today's
// weather per user and stores it on a daily_weather table — saves on
// geocoding + lets us run weather-correlation analytics on the dashboard.
// For v1, client-side fetch is fine.

import { supabase } from '@busy/api';

export type WeatherCondition =
  | 'sunny'
  | 'partly-cloudy'
  | 'cloudy'
  | 'rainy'
  | 'storm'
  | 'snow'
  | 'fog';

export type WeatherSnapshot = {
  condition: WeatherCondition;
  // Translation key for the condition label (resolved at render time so the tile
  // re-renders correctly when the language toggle flips). The key always lives in
  // apps/mobile/src/lib/i18n.ts under the `weather.*` namespace.
  labelKey: string;
  tempF: number; // current temperature, fahrenheit, rounded
  alert: boolean; // true for storms / severe weather
  locationLabel: string; // 'Houston, TX' style — derived from geocoding result
  fetchedAt: number; // ms since epoch — for cache busting
};

type GeocodeResult = {
  lat: number;
  lon: number;
  locationLabel: string;
};

// Cache prefix bumped v1 → v2 so any wrong-location entries cached
// against Open-Meteo's name-based geocoding get invalidated and
// re-fetched through the new zip_codes-first path.
const GEO_CACHE_PREFIX = 'busy_geo_v2_';
// v2 = labels switched to i18n keys; old v1 entries had `label` strings.
const WEATHER_CACHE_KEY = 'busy_weather_v2';
const WEATHER_TTL_MS = 60 * 60 * 1000; // 1 hour

const US_ZIP_REGEX = /^\d{5}$/;

export async function geocodeZip(zip: string): Promise<GeocodeResult | null> {
  const trimmed = zip.trim();
  if (!trimmed) return null;

  // Cache hit?
  const cacheKey = GEO_CACHE_PREFIX + trimmed;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached) as GeocodeResult;
  } catch {
    // localStorage might be unavailable (private browsing); fall through to fetch
  }

  // First-pass: query our own zip_codes table for US 5-digit zips. This
  // is the source of truth — deterministic and correct. Open-Meteo's
  // geocoding treats numeric zips as place-name searches and is
  // unreliable for US postal codes (a 5-digit zip can resolve to a
  // town in a completely different state with that number in its name).
  //
  // Wrapped in try/catch so any failure mode here (network blip, RLS
  // race, table not yet seeded, schema mismatch) silently falls through
  // to the Open-Meteo fallback below. We never want the supabase path
  // to be the reason the weather tile fails to render entirely.
  if (US_ZIP_REGEX.test(trimmed)) {
    try {
      const { data, error } = await supabase
        .from('zip_codes')
        .select('city, state, lat, lng')
        .eq('zip', trimmed)
        .maybeSingle();
      if (!error && data && data.lat != null && data.lng != null) {
        const result: GeocodeResult = {
          lat: data.lat as number,
          lon: data.lng as number,
          locationLabel: `${data.city}, ${data.state}`,
        };
        try { localStorage.setItem(cacheKey, JSON.stringify(result)); } catch { /* ignore */ }
        return result;
      }
      // Otherwise fall through to the Open-Meteo fallback below.
    } catch {
      // Defensive — fall through to Open-Meteo.
    }
  }

  // Fallback: Open-Meteo geocoding for international zips or anything
  // not in our table. countryCode=US biases to US results first; non-US
  // postal codes still work but with the name-match caveat noted above.
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
    trimmed
  )}&count=1&language=en&format=json&countryCode=US`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as {
      results?: Array<{
        latitude: number;
        longitude: number;
        name: string;
        admin1?: string; // state
        country_code?: string;
      }>;
    };
    const top = json.results?.[0];
    if (!top) return null;
    const locationLabel = top.admin1 ? `${top.name}, ${top.admin1}` : top.name;
    const result: GeocodeResult = {
      lat: top.latitude,
      lon: top.longitude,
      locationLabel,
    };
    try {
      localStorage.setItem(cacheKey, JSON.stringify(result));
    } catch {
      /* ignore */
    }
    return result;
  } catch {
    return null;
  }
}

export async function fetchWeather(geo: GeocodeResult): Promise<WeatherSnapshot | null> {
  // Cache hit (per-location, TTL'd)?
  try {
    const cached = localStorage.getItem(WEATHER_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as WeatherSnapshot & { lat: number; lon: number };
      const fresh = Date.now() - parsed.fetchedAt < WEATHER_TTL_MS;
      const sameLoc = parsed.lat === geo.lat && parsed.lon === geo.lon;
      if (fresh && sameLoc) return parsed;
    }
  } catch {
    /* ignore */
  }

  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${geo.lat}&longitude=${geo.lon}` +
    `&current=temperature_2m,weather_code&temperature_unit=fahrenheit&timezone=auto`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as {
      current?: { temperature_2m: number; weather_code: number };
    };
    const current = json.current;
    if (!current) return null;
    const { condition, labelKey, alert } = mapWmoCode(current.weather_code);
    const snapshot: WeatherSnapshot = {
      condition,
      labelKey,
      tempF: Math.round(current.temperature_2m),
      alert,
      locationLabel: geo.locationLabel,
      fetchedAt: Date.now(),
    };
    try {
      localStorage.setItem(
        WEATHER_CACHE_KEY,
        JSON.stringify({ ...snapshot, lat: geo.lat, lon: geo.lon })
      );
    } catch {
      /* ignore */
    }
    return snapshot;
  } catch {
    return null;
  }
}

/** One day in a 7-day forecast — same condition vocab as the current snapshot. */
export type ForecastDay = {
  date: string;          // ISO yyyy-mm-dd, local to the forecast's timezone
  condition: WeatherCondition;
  labelKey: string;
  highF: number;
  lowF: number;
  alert: boolean;
};

const FORECAST_CACHE_KEY = 'busy_forecast_v1';

/** Fetches 7 daily highs/lows + condition codes from Open-Meteo. Cached
 *  per location for 1 hour, same TTL as the current-conditions snapshot. */
export async function fetchSevenDayForecast(geo: GeocodeResult): Promise<ForecastDay[] | null> {
  try {
    const cached = localStorage.getItem(FORECAST_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as { days: ForecastDay[]; lat: number; lon: number; fetchedAt: number };
      const fresh = Date.now() - parsed.fetchedAt < WEATHER_TTL_MS;
      const sameLoc = parsed.lat === geo.lat && parsed.lon === geo.lon;
      if (fresh && sameLoc) return parsed.days;
    }
  } catch {
    /* ignore */
  }

  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${geo.lat}&longitude=${geo.lon}` +
    `&daily=temperature_2m_max,temperature_2m_min,weather_code` +
    `&temperature_unit=fahrenheit&timezone=auto&forecast_days=7`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as {
      daily?: {
        time: string[];
        temperature_2m_max: number[];
        temperature_2m_min: number[];
        weather_code: number[];
      };
    };
    const d = json.daily;
    if (!d || !d.time?.length) return null;
    const days: ForecastDay[] = d.time.map((date, i) => {
      const code = d.weather_code[i] ?? 0;
      const { condition, labelKey, alert } = mapWmoCode(code);
      return {
        date,
        condition,
        labelKey,
        highF: Math.round(d.temperature_2m_max[i] ?? 0),
        lowF: Math.round(d.temperature_2m_min[i] ?? 0),
        alert,
      };
    });
    try {
      localStorage.setItem(
        FORECAST_CACHE_KEY,
        JSON.stringify({ days, lat: geo.lat, lon: geo.lon, fetchedAt: Date.now() }),
      );
    } catch {
      /* ignore */
    }
    return days;
  } catch {
    return null;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Past-weather window for the dashboard's Revenue by Day chart.
// Matches the demo's bar-chart weather chips, but for HISTORY: each past
// bar gets a chip showing what the weather was that day, so a barber can
// correlate slow revenue with rainy/stormy days.
//
// Uses Open-Meteo's `past_days` param on the forecast endpoint — same
// API, same daily shape (high / low / weather_code). Capped at 92 days,
// which is the public limit on `past_days`. Ranges that need more (like
// YTD) get the most recent 92 days; older bars render without chips.
// ──────────────────────────────────────────────────────────────────────────

const PAST_WEATHER_MAX_DAYS = 92;
const PAST_WEATHER_CACHE_KEY = 'busy_past_weather_v1';

/** Fetches `pastDays` of historical daily weather (high / low / condition)
 *  through TODAY for the given geo. Returns null on failure. Caches per
 *  location + window size for 1 hour. */
export async function fetchPastWeather(
  geo: GeocodeResult,
  pastDays: number,
): Promise<ForecastDay[] | null> {
  const clampedDays = Math.max(0, Math.min(PAST_WEATHER_MAX_DAYS, pastDays));

  try {
    const cached = localStorage.getItem(PAST_WEATHER_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as {
        days: ForecastDay[];
        lat: number;
        lon: number;
        pastDays: number;
        fetchedAt: number;
      };
      const fresh = Date.now() - parsed.fetchedAt < WEATHER_TTL_MS;
      const sameLoc = parsed.lat === geo.lat && parsed.lon === geo.lon;
      const sameWindow = parsed.pastDays >= clampedDays;
      if (fresh && sameLoc && sameWindow) {
        // Trim to the requested window if the cached one was wider.
        return parsed.days.slice(-clampedDays - 1);
      }
    }
  } catch {
    /* ignore */
  }

  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${geo.lat}&longitude=${geo.lon}` +
    `&daily=temperature_2m_max,temperature_2m_min,weather_code` +
    `&temperature_unit=fahrenheit&timezone=auto` +
    `&past_days=${clampedDays}&forecast_days=1`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as {
      daily?: {
        time: string[];
        temperature_2m_max: number[];
        temperature_2m_min: number[];
        weather_code: number[];
      };
    };
    const d = json.daily;
    if (!d || !d.time?.length) return null;
    const days: ForecastDay[] = d.time.map((date, i) => {
      const code = d.weather_code[i] ?? 0;
      const { condition, labelKey, alert } = mapWmoCode(code);
      return {
        date,
        condition,
        labelKey,
        highF: Math.round(d.temperature_2m_max[i] ?? 0),
        lowF: Math.round(d.temperature_2m_min[i] ?? 0),
        alert,
      };
    });
    try {
      localStorage.setItem(
        PAST_WEATHER_CACHE_KEY,
        JSON.stringify({
          days,
          lat: geo.lat,
          lon: geo.lon,
          pastDays: clampedDays,
          fetchedAt: Date.now(),
        }),
      );
    } catch {
      /* ignore */
    }
    return days;
  } catch {
    return null;
  }
}

// WMO weather code → our condition / label-key / alert flag.
// Reference: https://open-meteo.com/en/docs (Weather Variables → Weather code WMO)
// `labelKey` is an i18n key — render-time resolution lets the tile flip language
// without re-fetching weather.
function mapWmoCode(code: number): {
  condition: WeatherCondition;
  labelKey: string;
  alert: boolean;
} {
  if (code === 0) return { condition: 'sunny', labelKey: 'weather.sunny', alert: false };
  if (code === 1) return { condition: 'sunny', labelKey: 'weather.mostlySunny', alert: false };
  if (code === 2) return { condition: 'partly-cloudy', labelKey: 'weather.partlyCloudy', alert: false };
  if (code === 3) return { condition: 'cloudy', labelKey: 'weather.cloudy', alert: false };
  if (code === 45 || code === 48) return { condition: 'fog', labelKey: 'weather.fog', alert: false };
  if (code >= 51 && code <= 57) return { condition: 'rainy', labelKey: 'weather.drizzle', alert: false };
  if (code === 61 || code === 63) return { condition: 'rainy', labelKey: 'weather.rain', alert: false };
  if (code === 65) return { condition: 'rainy', labelKey: 'weather.heavyRain', alert: true };
  if (code === 66 || code === 67) return { condition: 'rainy', labelKey: 'weather.freezingRain', alert: true };
  if (code === 71 || code === 73 || code === 77)
    return { condition: 'snow', labelKey: 'weather.snow', alert: false };
  if (code === 75) return { condition: 'snow', labelKey: 'weather.heavySnow', alert: true };
  if (code === 80 || code === 81) return { condition: 'rainy', labelKey: 'weather.showers', alert: false };
  if (code === 82) return { condition: 'rainy', labelKey: 'weather.heavyShowers', alert: true };
  if (code === 85 || code === 86) return { condition: 'snow', labelKey: 'weather.snowShowers', alert: false };
  if (code === 95) return { condition: 'storm', labelKey: 'weather.thunderstorms', alert: true };
  if (code === 96 || code === 99) return { condition: 'storm', labelKey: 'weather.severeStorms', alert: true };
  return { condition: 'cloudy', labelKey: 'weather.overcast', alert: false };
}

// ============================ tip kind picker ============================
//
// Picks the most-relevant capacity-management tip for a given forecast day.
// One bucket per kind keeps the tip set focused — the WeatherTip modal looks
// up the i18n keys `weather.tip.{kind}.{title|tagline|b1|b2|b3}` to render.
//
// Priority (first match wins):
//   1. storm     — condition='storm' (severe weather, includes alert=true)
//   2. snow      — condition='snow'
//   3. rain      — condition='rainy'
//   4. heat      — clear-ish day with high >= 95°F
//   5. cold      — any day with low <= 25°F
//   6. fog       — condition='fog'
//   7. mild weather (sunny/partly-cloudy/cloudy at moderate temp) is then
//      split by the barber's day-of-week pattern:
//        - peak or average DOW → 'clear'    (Prime walk-in day playbook)
//        - slow DOW            → 'quietDay' (Slow-day playbook — text
//                                            regulars due, use downtime)
//
// Snow/rain are checked before heat/cold so a snowy 20°F day shows the snow
// playbook (which is more actionable) rather than the generic cold tip.
//
// Inclement weather always wins regardless of DOW — peak/slow doesn't change
// the right advice for storm / snow / rain / heat / cold / fog days. Only
// the mild-weather case branches on the barber's pattern, since that's
// where "is today actually a prime walk-in day?" is the meaningful question.

import type { DayOfWeekIntensity } from './dayOfWeekPattern';

export type WeatherTipKind =
  | 'storm'
  | 'snow'
  | 'rain'
  | 'heat'
  | 'cold'
  | 'fog'
  | 'clear'
  | 'quietDay';

export function weatherTipKind(
  day: ForecastDay,
  dowIntensity: DayOfWeekIntensity = 'average',
): WeatherTipKind {
  if (day.condition === 'storm') return 'storm';
  if (day.condition === 'snow')  return 'snow';
  if (day.condition === 'rainy') return 'rain';
  if (day.highF >= 95)           return 'heat';
  if (day.lowF  <= 25)           return 'cold';
  if (day.condition === 'fog')   return 'fog';
  if (dowIntensity === 'slow')   return 'quietDay';
  return 'clear';
}
